import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Button,
  Group,
  PageHeader,
  Paper,
  Stack,
  TabView,
  TextInput,
} from '@vipso/shared';
import {
  useChangeSystemAdminPasswordMutation,
  useUpdateSystemAdminProfileMutation,
} from '../api/adminApi';
import { setInit } from '../store/authSlice';
import type { RootState } from '../store/index';

export function AccountSettingsPage() {
  const dispatch = useDispatch();
  const admin = useSelector((state: RootState) => state.auth.admin);
  const socketRooms = useSelector((state: RootState) => state.auth.socketRooms);

  const [name, setName] = useState(admin?.name ?? '');
  const [phone, setPhone] = useState(admin?.phone ?? '');
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [profileMsg, setProfileMsg] = useState<string | null>(null);
  const [passwordMsg, setPasswordMsg] = useState<string | null>(null);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const [updateProfile, profileState] = useUpdateSystemAdminProfileMutation();
  const [changePassword, passwordState] = useChangeSystemAdminPasswordMutation();

  useEffect(() => {
    setName(admin?.name ?? '');
    setPhone(admin?.phone ?? '');
  }, [admin]);

  async function handleProfileSave() {
    setProfileMsg(null);
    setProfileError(null);
    try {
      const result = await updateProfile({
        name: name.trim(),
        phone: phone.trim() || undefined,
      }).unwrap();
      dispatch(
        setInit({
          admin: {
            id: result.updateSystemAdminProfile.id,
            name: result.updateSystemAdminProfile.name,
            email: result.updateSystemAdminProfile.email,
            phone: result.updateSystemAdminProfile.phone,
          },
          socketRooms,
        }),
      );
      setProfileMsg('Profile updated.');
    } catch {
      setProfileError('Could not update profile.');
    }
  }

  async function handlePasswordSave() {
    setPasswordMsg(null);
    setPasswordError(null);
    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match.');
      return;
    }
    if (newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters.');
      return;
    }
    try {
      await changePassword({ oldPassword, newPassword }).unwrap();
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordMsg('Password changed.');
    } catch {
      setPasswordError('Could not change password. Check your current password.');
    }
  }

  return (
    <Stack gap="lg">
      <PageHeader
        title="Account settings"
        description="Update your profile and password."
      />

      <TabView
        defaultValue="profile"
        tabs={[
          {
            value: 'profile',
            title: 'Edit profile',
            content: (
              <Paper p="lg" withBorder radius="md">
                <Stack gap="md">
                  {profileMsg ? <Alert color="green" title={profileMsg} /> : null}
                  {profileError ? <Alert color="red" title={profileError} /> : null}
                  <TextInput label="Email" value={admin?.email ?? ''} disabled />
                  <TextInput
                    label="Name"
                    value={name}
                    onChange={(e) => setName(e.currentTarget.value)}
                    required
                  />
                  <TextInput
                    label="Phone"
                    value={phone}
                    onChange={(e) => setPhone(e.currentTarget.value)}
                  />
                  <Group justify="flex-end">
                    <Button
                      loading={profileState.isLoading}
                      disabled={!name.trim()}
                      onClick={() => void handleProfileSave()}
                    >
                      Save profile
                    </Button>
                  </Group>
                </Stack>
              </Paper>
            ),
          },
          {
            value: 'password',
            title: 'Change password',
            content: (
              <Paper p="lg" withBorder radius="md">
                <Stack gap="md">
                  {passwordMsg ? <Alert color="green" title={passwordMsg} /> : null}
                  {passwordError ? <Alert color="red" title={passwordError} /> : null}
                  <TextInput
                    label="Current password"
                    type="password"
                    value={oldPassword}
                    onChange={(e) => setOldPassword(e.currentTarget.value)}
                    required
                  />
                  <TextInput
                    label="New password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.currentTarget.value)}
                    required
                  />
                  <TextInput
                    label="Confirm new password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.currentTarget.value)}
                    required
                  />
                  <Group justify="flex-end">
                    <Button
                      loading={passwordState.isLoading}
                      disabled={!oldPassword || !newPassword || !confirmPassword}
                      onClick={() => void handlePasswordSave()}
                    >
                      Change password
                    </Button>
                  </Group>
                </Stack>
              </Paper>
            ),
          },
        ]}
      />
    </Stack>
  );
}
